import { Card, Col, Row, Spinner, Alert } from 'react-bootstrap';
import Stat from '../../components/project-management/Stat';
import ProductionOrdersCopperReview from '../../components/project-management/OrdersCopperReview';
import ProductionOrdersOvcQtyReview from '../../components/charts/production-management-charts/ProductionOrdersPvcQtyReview';
import ExpandableActivityCard from '../../components/timelines/ExpandableActivityCard';
import ProjectDashboardTable from '../../components/tables/ProjectDashboardTable';

// import { useProductionDashboardStats } from '../../features/production-management/hooks/useProductionDashboardStats';
import { buildProductionStats } from '../../data/project-management/stats.js';
import {
  useProductionDashboardStats,
  useProductionMaterialTotals
} from '../../features/jobs/hooks/useProductionDashboardStats';
import ProductionMaterialBarChart from '../../components/charts/production-management-charts/ProductionMaterialBarChart.js';
import {
  useFactoryActivity,
  useFactoryReports
} from '../../features/jobs/hooks/useJob.js';
import { useMemo } from 'react';
import TestResetPanel from '../../components/project-management/TestResetPanel';
import { useAuthContext } from '../../providers/AuthProvider';
import { formatMass, exactMass } from '../../helpers/formatMass';

const ProductionManagement = () => {
  const [{ user }] = useAuthContext();
  const { data, isLoading, isError, error } = useProductionDashboardStats();
  const { data: activityTimelineData, isLoading: isActivityLoading } =
    useFactoryActivity(100);
  const { data: factoryReportsData, isLoading: isFactoryReportsLoading } =
    useFactoryReports(100);
  console.log('factory reports data:', factoryReportsData);
  const reportTimelineData = useMemo(() => {
    return Array.isArray(factoryReportsData?.data)
      ? factoryReportsData.data
      : [];
  }, [factoryReportsData]);

  const stats = buildProductionStats(data?.stats);

  return (
    <>
      <Row className="gy-3 mb-6 justify-content-between align-items-center">
        <Col xl={4} lg={12}>
          <div className="d-flex flex-column justify-content-center h-100">
            <h2 className="mb-2 text-1100">Production Dashboard</h2>
            <h5 className="text-700 fw-semi-bold mb-0">
              Here’s what’s going on in production right now
            </h5>
          </div>
        </Col>

        <Col xl={8} lg={12}>
          <ProductionMaterialTotalsHeader />
        </Col>
      </Row>
      {isError && (
        <Alert variant="danger" className="mb-3">
          Failed to load production stats: {error?.message || 'Unknown error'}
        </Alert>
      )}

      <Row className="mb-3 gy-6">
        <Col xs={12} xxl={2}>
          <Row className="align-items-center g-3 g-xxl-0 h-100 align-content-between">
            {isLoading ? (
              <Col xs={12}>
                <div className="d-flex align-items-center justify-content-center h-100 py-4">
                  <Spinner animation="border" size="sm" className="me-2" />
                  <span>Loading stats...</span>
                </div>
              </Col>
            ) : (
              stats.map(stat => (
                <Col
                  xs={12}
                  sm={6}
                  md={3}
                  lg={6}
                  xl={3}
                  xxl={12}
                  key={stat.title}
                >
                  <Stat stat={stat} />
                </Col>
              ))
            )}
          </Row>
        </Col>

        <Col xs={12} xl={6} xxl={5}>
          <h3>Copper Quantity</h3>
          <ProductionMaterialBarChart
            height="316px"
            width="100%"
            filterCategory="copper"
          />
        </Col>

        <Col xs={12} xl={6} xxl={5}>
          <h3>Plastic &amp; BLR Consumption</h3>
          <ProductionMaterialBarChart
            height="316px"
            width="100%"
            filterCategory={['plastic', 'blr']}
          />
        </Col>
      </Row>

      <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white pt-7 pb-3 border-y border-300 mb-3">
        <Row>
          <Col xs={12} xl={7} xxl={6}>
            <ProductionOrdersCopperReview />
          </Col>
          <Col xs={12} xl={5} xxl={6}>
            <h3>Raw ingredients for future projects</h3>
            <p className="text-700 mb-0 mb-xl-3">Deadline &amp; progress</p>
            <ProductionOrdersOvcQtyReview />
          </Col>
        </Row>
      </div>

      <div className="mx-lg-n4 mb-3">
        <Row className="g-3">
          <Col xs={12} xl={6} xxl={6}>
            <ExpandableActivityCard
              title="Activity"
              seeAllTo="/apps/activity"
              subtitle="Recent activity across all machines"
              reports={activityTimelineData}
              loading={isActivityLoading}
            />
          </Col>
          <Col xs={12} xl={6} xxl={6}>
            <ExpandableActivityCard
              title="Report"
              seeAllTo="/apps/reports"
              subtitle="Reports across all machines"
              reports={reportTimelineData}
              loading={isFactoryReportsLoading}
            />
          </Col>
        </Row>
      </div>

      <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white pt-6 border-top border-300">
        <h3>Projects</h3>
        <p className="text-700 lh-sm mb-4">
          Brief summary of all final phase projects
        </p>
        <ProjectDashboardTable />
      </div>

      {/* Admin only — and this is the one place the "everybody sees everything"
          rule is deliberately not applied. That rule is about business screens:
          a salesperson reading production numbers costs nothing, and the 403
          stops them acting. A row of DELETE EVERYTHING buttons is not a screen
          to read, and the server would refuse them anyway; showing it to the
          whole factory only invites the click. */}
      {user?.role === 'admin' && <TestResetPanel />}
    </>
  );
};

export default ProductionManagement;



const MiniTotalCard = ({ title, value, borderClass }) => {
  return (
    <Card className={`h-100 shadow-sm ${borderClass || ''}`}>
      <Card.Body className="py-3">
        <div className="text-700 fs-9 fw-semibold text-uppercase mb-1 text-nowrap">
          {title}
        </div>
        {/* `text-nowrap` is deliberate — a weight broken across two lines is
            unreadable — which is exactly why the number has to be short. A
            hundred tonnes written in kilograms simply left the card. */}
        <div className="fs-4 fw-bold text-1100 text-nowrap" title={exactMass(value)}>
          {formatMass(value)}
        </div>
      </Card.Body>
    </Card>
  );
};

const ProductionMaterialTotalsHeader = () => {
  const { data, isLoading, isError, error } = useProductionMaterialTotals();

  if (isLoading) {
    return (
      <div className="d-flex align-items-center justify-content-end py-2">
        <Spinner animation="border" size="sm" className="me-2" />
        <span>Loading totals...</span>
      </div>
    );
  }

  if (isError) {
    return (
      <Alert variant="danger" className="mb-0 py-2">
        Failed to load totals: {error?.message || 'Unknown error'}
      </Alert>
    );
  }

  const totals = data?.totals || {};

  return (
    <Row className="g-2 justify-content-end">
      <Col xs={6} md={3}>
        <MiniTotalCard
          title="Copper Total"
          value={totals.copper_kg}
          borderClass="border-start border-4 border-warning"
        />
      </Col>
      <Col xs={6} md={3}>
        <MiniTotalCard
          title="PVC Total"
          value={totals.pvc_kg}
          borderClass="border-start border-4 border-info"
        />
      </Col>
      <Col xs={6} md={3}>
        <MiniTotalCard
          title="PE Total"
          value={totals.polietilen_kg}
          borderClass="border-start border-4 border-success"
        />
      </Col>
      <Col xs={6} md={3}>
        <MiniTotalCard
          title="BLR Total"
          value={totals.blr_kg}
          borderClass="border-start border-4 border-secondary"
        />
      </Col>
    </Row>
  );
};
